class HeaderComponent extends HTMLElement {
  #initialHeaderTop = 0;
  #scrollHandler = null;
  #inertElements = [];
  #closeTimer = null;
  #focusTimer = null;
  #desktopMedia = null;

  constructor() {
    super();

    this.menuToggle = null;
    this.menuClose = null;
    this.mobileMenuOverlay = null;
    this.mobileMenu = null;
  }

  connectedCallback() {
    this.menuToggle = this.querySelector('[data-ref="menu-toggle"]');
    this.menuClose = this.querySelector('[data-ref="menu-close"]');
    this.mobileMenuOverlay = this.querySelector('[data-ref="mobile-menu-overlay"]');
    this.mobileMenu = this.querySelector('[data-ref="mobile-menu"]');
    this.#desktopMedia = window.matchMedia('(width > 1000px)');

    if (this.menuToggle) {
      this.menuToggle.addEventListener('click', this.#handleMenuOpen);
    }

    if (this.menuClose) {
      this.menuClose.addEventListener('click', this.#handleMenuClose);
    }

    if (this.mobileMenuOverlay) {
      this.mobileMenuOverlay.addEventListener('click', this.#handleOverlayClick);
    }

    document.addEventListener('keydown', this.#handleKeyDown);
    this.#desktopMedia.addEventListener('change', this.#handleViewportChange);

    // Initialiser l'observation sticky si le header est sticky
    this.#initStickyObserver();
  }

  disconnectedCallback() {
    if (this.menuToggle) {
      this.menuToggle.removeEventListener('click', this.#handleMenuOpen);
    }

    if (this.menuClose) {
      this.menuClose.removeEventListener('click', this.#handleMenuClose);
    }

    if (this.mobileMenuOverlay) {
      this.mobileMenuOverlay.removeEventListener('click', this.#handleOverlayClick);
    }

    document.removeEventListener('keydown', this.#handleKeyDown);
    this.#desktopMedia?.removeEventListener('change', this.#handleViewportChange);

    window.clearTimeout(this.#closeTimer);
    window.clearTimeout(this.#focusTimer);
    document.body.classList.remove('overflow-hidden');
    this.#setPageInert(false);

    // Nettoyer l'observer
    this.#cleanupStickyObserver();
  }

  #handleMenuOpen = () => {
    this.openMobileMenu();
  };

  #handleMenuClose = () => {
    this.closeMobileMenu();
  };

  #handleOverlayClick = (event) => {
    // Fermer le menu si on clique sur l'overlay (pas sur le menu lui-même)
    if (event.target === this.mobileMenuOverlay) {
      this.closeMobileMenu();
    }
  };

  #handleKeyDown = (event) => {
    if (!this.mobileMenuOverlay?.classList.contains('is-open')) {
      return;
    }

    if (event.key === 'Escape') {
      this.closeMobileMenu();
      return;
    }

    if (event.key === 'Tab') {
      this.#trapFocus(event);
    }
  };

  #handleViewportChange = (event) => {
    if (event.matches && this.mobileMenuOverlay?.classList.contains('is-open')) {
      this.closeMobileMenu(true);
    }
  };

  openMobileMenu() {
    if (!this.mobileMenuOverlay || !this.mobileMenu) {
      return;
    }

    window.clearTimeout(this.#closeTimer);
    window.clearTimeout(this.#focusTimer);
    this.mobileMenuOverlay.classList.remove('is-closing');
    this.mobileMenuOverlay.classList.add('is-open');
    this.mobileMenuOverlay.setAttribute('aria-hidden', 'false');
    this.menuToggle?.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden');
    this.#setPageInert(true);

    this.#focusTimer = window.setTimeout(() => {
      this.menuClose?.focus();
    }, 50);
  }

  closeMobileMenu(immediate = false) {
    if (!this.mobileMenuOverlay?.classList.contains('is-open')) {
      return;
    }

    window.clearTimeout(this.#closeTimer);
    window.clearTimeout(this.#focusTimer);
    this.menuToggle?.setAttribute('aria-expanded', 'false');
    this.mobileMenuOverlay.classList.add('is-closing');

    const delay = immediate ? 0 : this.#getTransitionDuration();
    this.#closeTimer = window.setTimeout(() => this.#finishClose(!immediate), delay);
  }

  #finishClose(returnFocus) {
    this.mobileMenuOverlay?.classList.remove('is-open', 'is-closing');
    this.mobileMenuOverlay?.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
    this.#setPageInert(false);
    if (returnFocus) {
      this.menuToggle?.focus();
    }
  }

  #getTransitionDuration() {
    if (!this.mobileMenu || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return 0;
    }

    const styles = window.getComputedStyle(this.mobileMenu);
    const durations = styles.transitionDuration.split(',').map(this.#cssTimeToMilliseconds);
    const delays = styles.transitionDelay.split(',').map(this.#cssTimeToMilliseconds);

    return Math.max(
      0,
      ...durations.map((duration, index) => duration + (delays[index % delays.length] || 0))
    ) + 20;
  }

  #cssTimeToMilliseconds(value) {
    const parsedValue = Number.parseFloat(value);
    if (Number.isNaN(parsedValue)) {
      return 0;
    }

    return value.trim().endsWith('ms') ? parsedValue : parsedValue * 1000;
  }

  #trapFocus(event) {
    const focusableElements = Array.from(
      this.mobileMenu.querySelectorAll(
        'a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.getClientRects().length > 0 && element.getAttribute('aria-hidden') !== 'true');

    if (focusableElements.length === 0) {
      event.preventDefault();
      this.mobileMenu.focus();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && (document.activeElement === firstElement || !this.mobileMenu.contains(document.activeElement))) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  #setPageInert(shouldBeInert) {
    if (!shouldBeInert) {
      this.#inertElements.forEach(({ element, wasInert }) => {
        element.inert = wasInert;
      });
      this.#inertElements = [];
      return;
    }

    if (this.#inertElements.length > 0) {
      return;
    }

    const headerGroup = this.closest('.shopify-section-group-header-group') || this;
    this.#inertElements = Array.from(document.body.children)
      .filter((element) => element !== headerGroup && !element.contains(headerGroup))
      .map((element) => {
        const wasInert = element.inert;
        element.inert = true;
        return { element, wasInert };
      });
  }

  /**
   * Initialise l'observation du header pour détecter quand il devient sticky
   */
  #initStickyObserver() {
    // Vérifier si le header est configuré comme sticky
    const headerGroup = this.closest('.shopify-section-group-header-group');
    if (!headerGroup || getComputedStyle(headerGroup).position !== 'sticky') {
      return;
    }

    // Calculer la position initiale réelle du header
    document.querySelectorAll('.shopify-section-group-header-group').forEach((section) => {
      if (section === headerGroup) {
        return;
      }
      this.#initialHeaderTop += section.offsetHeight;
    });

    // Créer le gestionnaire de scroll avec throttling
    this.#scrollHandler = this.#throttle(() => {
      this.#checkStickyState();
    }, 10);

    // Ajouter l'écouteur de scroll
    window.addEventListener('scroll', this.#scrollHandler, { passive: true });

    // Vérifier l'état initial
    this.#checkStickyState();
  }

  /**
   * Vérifie l'état sticky du header
   */
  #checkStickyState() {
    if (this.#initialHeaderTop === null) return;

    // Le header est sticky quand le scroll dépasse sa position initiale
    const isSticky = window.scrollY >= this.#initialHeaderTop;

    this.dataset.stickyState = isSticky ? 'active' : 'inactive';
  }

  /**
   * Throttle function pour optimiser les performances
   */
  #throttle(func, limit) {
    let inThrottle;
    return function () {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => (inThrottle = false), limit);
      }
    };
  }

  /**
   * Nettoie l'observer lors de la destruction du composant
   */
  #cleanupStickyObserver() {
    if (this.#scrollHandler) {
      window.removeEventListener('scroll', this.#scrollHandler);
      this.#scrollHandler = null;
    }
    this.#initialHeaderTop = 0;
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}
